import {ComponentProps, MouseEventHandler, VFC, useState} from 'react';
import {EzButton, EzModal} from '@ezcater/recipe';
import gql from 'graphql-tag';
import {useFlashMessages} from '@ezcater/flash-messages';
import {ClassNames} from '@emotion/react';
import {useMutation} from 'services/apollo';
import {
  DeliveryStatus,
  DriverAssignmentStatusUpdateInputStatus,
  type DriverProgressStatusUpdateMutation,
  type DriverProgressStatusUpdateMutationVariables,
} from 'services/graphql/types';
import styled from '@emotion/styled';
import {useTranslation} from 'react-i18next';
import {trackButtonClick} from 'util/tracking';
import StartDeliveryButton from './StartDeliveryButton';
import CompleteDeliveryButton from './CompleteDeliveryButton';

const Container = styled.div`
  background-color: #fff;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);
  margin-top: 1rem;
  padding: 1rem 1.5rem 1rem 1.5rem;

  & > * {
    height: 100%;
    width: 100%;
    padding: 0.5rem 0 0.5rem 0;
  }
`;

export const AssignmentStatusUpdateMutation = gql(/* GraphQL */ `
  mutation DriverProgressStatusUpdateMutation($input: DriverAssignmentStatusUpdateInput!) {
    assignmentStatusUpdate: driverAssignmentStatusUpdate(input: $input) {
      assignment {
        id
        status
      }
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

interface Props {
  readonly eventAt: string;
  readonly driverAssignmentId: string;
  readonly status: `${DeliveryStatus}`;
}

// Map to relate the status to the
const STATUS_MAP = {
  [DeliveryStatus.Assigned]: DriverAssignmentStatusUpdateInputStatus.PickedUp,
  [DeliveryStatus.InProgress]: DriverAssignmentStatusUpdateInputStatus.DroppedOff,
} as const;

function isValidStatus(x: string): x is keyof typeof STATUS_MAP {
  return x in STATUS_MAP;
}

type PrimaryButtonProps = Omit<ComponentProps<typeof EzButton>, 'use'>;

type ButtonProps = Pick<Props, 'driverAssignmentId'> &
  PrimaryButtonProps & {
    /**
     * The status to transition to next.
     */
    readonly nextStatus: (typeof STATUS_MAP)[keyof typeof STATUS_MAP];
    /**
     * The current status
     */
    readonly status: keyof typeof STATUS_MAP;
    readonly eventAt: string;
  };

const UpdateDeliveryButton: VFC<ButtonProps> = ({
  driverAssignmentId,
  status,
  nextStatus,
  eventAt,
}) => {
  const {t} = useTranslation();
  const errorLabel = t('driverProgress.actionButtons.error');
  const uncaughtErrorMessage = t('driverProgress.actionButtons.uncaughtError');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalIsSubmitting, setModalIsSubmitting] = useState(false);
  const {addFlashMessage} = useFlashMessages();
  const [updateDelivery, {loading}] = useMutation<
    DriverProgressStatusUpdateMutation,
    DriverProgressStatusUpdateMutationVariables
  >(AssignmentStatusUpdateMutation, {
    variables: {
      input: {
        assignmentId: driverAssignmentId,
        status: nextStatus,
      },
    },
    onCompleted(result) {
      result?.assignmentStatusUpdate?.userErrors?.forEach(error => {
        addFlashMessage({
          type: 'error',
          headline: errorLabel,
          message: error.message,
        });
      });
    },
    onError() {
      addFlashMessage({
        type: 'error',
        headline: errorLabel,
        message: uncaughtErrorMessage,
      });
    },
  });

  const handleStartDelivery: MouseEventHandler<HTMLButtonElement> = async () => {
    await updateDelivery();

    trackButtonClick('driver-picked-up-button', {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
  };

  const handleModalOpen = () => {
    setModalIsOpen(true);
  };

  const handleCompleteDelivery = async () => {
    await updateDelivery();

    trackButtonClick('driver-dropped-off-button', {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
    setModalIsSubmitting(false);
    setModalIsOpen(false);
  };

  const handleModalDismiss = async () => {
    setModalIsOpen(false);

    trackButtonClick('complete-delivery-modal-dismiss-button', {
      misc_json: JSON.stringify({
        driverAssignmentId,
      }),
    });
  };

  if (status === DeliveryStatus.Assigned) {
    return (
      <StartDeliveryButton loading={loading} eventAt={eventAt} onClick={handleStartDelivery} />
    );
  }

  if (status === DeliveryStatus.InProgress) {
    const confirmationText = t('driverProgress.completeDelivery.modal.confirmationMessage');
    const confirmLabel = t('driverProgress.completeDelivery.modal.confirm');
    const cancelLabel = t('driverProgress.completeDelivery.modal.cancel');
    const header = t('driverProgress.completeDelivery.modal.header');

    return (
      <>
        <ClassNames>
          {({css}) => (
            <EzModal
              slotProps={{
                dialog: {
                  className: css`
                    @media (max-width: 768px) {
                      height: 50%;
                      width: 90%;
                    }
                  `,
                },
              }}
              isSubmitting={modalIsSubmitting}
              isOpen={modalIsOpen}
              onSubmit={() => handleCompleteDelivery()}
              submitLabel={confirmLabel}
              onDismiss={() => handleModalDismiss()}
              dismissLabel={cancelLabel}
              headerText={header}
            >
              {confirmationText}
            </EzModal>
          )}
        </ClassNames>
        <CompleteDeliveryButton loading={loading} onClick={handleModalOpen} />
      </>
    );
  }

  return null;
};

/**
 * Provides the appropriate action given the current state of some order
 * If no action is available for a given state, returns null
 *
 * @param param0
 * @returns
 */
const ActionButton: VFC<Props> = ({driverAssignmentId, status, eventAt}) => {
  if (!isValidStatus(status)) {
    return null;
  }

  const nextStatus = STATUS_MAP[status];

  return (
    <Container>
      <UpdateDeliveryButton
        status={status}
        nextStatus={nextStatus}
        eventAt={eventAt}
        driverAssignmentId={driverAssignmentId}
      />
    </Container>
  );
};

export default ActionButton;
